/**
 * What an intake email will put in front of the client, on its approval card:
 * the intake (title, size, every question), the note at the top of the form,
 * when the link expires, and the client's own page in a dialog.
 */
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  ExternalLink,
  Eye,
  Loader2,
  Monitor,
  Smartphone
} from "lucide-react";
import { lazy, Suspense, useMemo, useState, type FC } from "react";
import { Link } from "react-router-dom";

import { OutgoingIntakesAPI } from "@/api/outgoingIntakesApi";
import type {
  OutgoingIntake,
  OutgoingIntakeFormNode,
  OutgoingIntakeQuestionType
} from "@/api/types/outgoingIntake";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import type { ApprovalPreview } from "@/features/agents/types";
import { useArtifactData } from "@/features/agents/hooks/useArtifactData";
import { intakeEstimate } from "@/features/foreignNationalOutgoingIntakes/intakeEstimate";
import { usePreviewInitialData } from "@/features/requestsIde/editor/previewData";
import { intakePath } from "@/features/requestsIde/routes";
import { cn } from "@/lib/utils";

const PublicOutgoingIntake = lazy(() =>
  import("@/pages/PublicOutgoingIntake/PublicOutgoingIntake").then((m) => ({
    default: m.PublicOutgoingIntake
  }))
);

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null;

const TYPE_LABEL: Record<OutgoingIntakeQuestionType, string> = {
  short_text: "Short answer",
  long_text: "Long answer",
  boolean: "Yes / No",
  date: "Date",
  single_select: "Choice",
  multi_select: "Choices",
  file: "Upload",
  address: "Address"
};

type OutlineQuestion = {
  id: string;
  prompt: string;
  required: boolean;
  kind: string | null;
};

type OutlineSection = {
  id: string;
  title: string | null;
  questions: OutlineQuestion[];
};

const questionsOf = (
  nodes: OutgoingIntakeFormNode[] | undefined
): OutlineQuestion[] =>
  (nodes ?? []).flatMap((node) =>
    node.node_type === "question"
      ? [
          {
            id: node.node_id,
            prompt: (node.prompt || node.title || "Untitled question").trim(),
            required: Boolean(node.required),
            kind: node.question_type ? TYPE_LABEL[node.question_type] : null
          }
        ]
      : questionsOf(node.children)
  );

/**
 * The schema as the client meets it: each top-level section with every
 * question under it (repeatable groups and nested sections flattened), and
 * questions outside any section in sections of their own.
 */
export const intakeOutline = (
  schema: OutgoingIntakeFormNode[] | undefined
): OutlineSection[] => {
  const out: OutlineSection[] = [];
  let loose: OutlineSection | null = null;
  for (const node of schema ?? []) {
    const isSection = node.node_type === "section";
    const questions = questionsOf(isSection ? node.children : [node]);
    if (questions.length && isSection) {
      out.push({
        id: node.node_id,
        title: node.title?.trim() || null,
        questions
      });
      loose = null;
    } else if (questions.length && loose) {
      loose.questions.push(...questions);
    } else if (questions.length) {
      loose = { id: `loose:${node.node_id}`, title: null, questions };
      out.push(loose);
    }
  }
  return out;
};

const countLabel = (n: number, noun: string) =>
  `${n} ${noun}${n === 1 ? "" : "s"}`;

/** Past this, a request is a questionnaire; the card says so before it goes out. */
const LONG_INTAKE = 40;
const UPLOADS_SHOWN = 4;

const Outline: FC<{ sections: OutlineSection[] }> = ({ sections }) => {
  let n = 0;
  return (
    <div
      className="-mx-2.5 flex max-h-60 flex-col gap-2.5 overflow-auto border-t px-2.5 pt-2"
      data-testid="intake-preview-outline"
    >
      {sections.map((section) => (
        <div key={section.id} className="flex flex-col gap-1">
          {section.title ? (
            <span className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
              {section.title}
            </span>
          ) : null}
          {section.questions.map((q) => {
            n += 1;
            return (
              <div key={q.id} className="flex items-baseline gap-1.5 text-xs">
                <span className="w-4 shrink-0 text-right text-xs text-fg-4">
                  {n}
                </span>
                <span className="min-w-0 flex-1 text-foreground">
                  {q.prompt}
                  {q.required ? (
                    <span
                      className="text-muted-foreground"
                      aria-label="required"
                    >
                      {" "}
                      *
                    </span>
                  ) : null}
                </span>
                {q.kind ? (
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {q.kind}
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

type Device = "desktop" | "mobile";

const ClientPreviewDialog: FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  intake: OutgoingIntake;
  note: string | null;
  clientName: string | null;
  editHref: string | null;
}> = ({ open, onOpenChange, intake, note, clientName, editHref }) => {
  const [device, setDevice] = useState<Device>("desktop");
  const initialData = usePreviewInitialData(intake, intake.schema ?? [], null, {
    attorneyNote: note,
    clientName
  });
  const firstName = (clientName ?? "").trim().split(/\s+/)[0] || "the client";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex h-[86vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-250"
        showCloseButton={false}
        data-testid="intake-client-preview"
      >
        <div className="flex h-11 shrink-0 items-center gap-2 border-b pr-3 pl-4">
          <DialogTitle className="min-w-0 truncate text-base font-medium">
            {intake.title || "Intake"}
          </DialogTitle>
          <span className="inline-flex h-5 shrink-0 items-center gap-1 rounded-full bg-muted px-2 text-xs font-medium text-muted-foreground">
            <Eye className="size-3" /> What {firstName} sees
          </span>
          <DialogDescription className="sr-only">
            A preview of the intake as the client will see it. Nothing is saved
            or sent.
          </DialogDescription>
          <span className="flex-1" />
          <ToggleGroup
            type="single"
            value={device}
            onValueChange={(v) => v && setDevice(v as Device)}
            className="shrink-0 gap-0.5 rounded-md bg-muted p-0.5"
            aria-label="Preview width"
          >
            <ToggleGroupItem
              value="desktop"
              aria-label="Desktop"
              className="h-6 w-7 min-w-0 rounded-sm px-0 text-muted-foreground hover:bg-transparent hover:text-foreground aria-checked:bg-background aria-checked:text-foreground aria-checked:shadow-sm"
            >
              <Monitor className="size-3.5" />
            </ToggleGroupItem>
            <ToggleGroupItem
              value="mobile"
              aria-label="Phone"
              className="h-6 w-7 min-w-0 rounded-sm px-0 text-muted-foreground hover:bg-transparent hover:text-foreground aria-checked:bg-background aria-checked:text-foreground aria-checked:shadow-sm"
            >
              <Smartphone className="size-3.5" />
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
        <div className="flex min-h-0 flex-1 items-stretch justify-center bg-fill-1 p-4">
          <div
            className={cn(
              "relative min-h-0 w-full overflow-hidden rounded-lg border border-border bg-background",
              device === "mobile" ? "max-w-100" : "max-w-240"
            )}
          >
            {initialData ? (
              <Suspense
                fallback={
                  <div className="flex h-full items-center justify-center">
                    <Spinner className="size-4" />
                  </div>
                }
              >
                <PublicOutgoingIntake
                  key={`${intake.outgoing_intake_id}-${device}`}
                  mode="preview"
                  hideTopBar
                  embeddedPreview
                  previewViewport="responsive"
                  tokenOverride="__preview__"
                  initialData={initialData}
                />
              </Suspense>
            ) : null}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2 border-t py-2 pr-3 pl-4">
          <span className="min-w-0 flex-1 text-xs text-muted-foreground">
            Preview only. Nothing is saved and nothing is sent until you
            approve.
          </span>
          {editHref ? (
            <Button asChild variant="ghost" size="sm" className="text-xs">
              <Link to={editHref} target="_blank" rel="noreferrer">
                Edit in Intake Requests <ExternalLink className="size-3" />
              </Link>
            </Button>
          ) : null}
          <Button
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={() => onOpenChange(false)}
          >
            Back to approval
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

type Props = {
  preview: Extract<ApprovalPreview, { kind: "intake" }>;
  /** The pending call's arguments (`note`, `expires_in_days`). */
  input: unknown;
  clientName: string | null;
};

export const IntakeApprovalPreview: FC<Props> = ({
  preview,
  input,
  clientName
}) => {
  const fnId = preview.foreign_national_id;
  const { data, loading, error } = useArtifactData(
    () =>
      fnId
        ? OutgoingIntakesAPI.get(fnId, preview.id).then(
            (r) => r.outgoing_intake
          )
        : Promise.reject(new Error("No client for this intake.")),
    [fnId, preview.id],
    { clientId: fnId, invalidateOn: ["intakes"] }
  );
  const [showOutline, setShowOutline] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const note =
    isRecord(input) && typeof input.note === "string" && input.note.trim()
      ? input.note.trim()
      : null;
  const expires =
    isRecord(input) && typeof input.expires_in_days === "number"
      ? input.expires_in_days
      : null;
  const sections = useMemo(() => intakeOutline(data?.schema), [data]);
  const editHref = fnId ? intakePath(fnId, preview.id) : null;

  if (loading && !data) {
    return (
      <div
        className="flex items-center gap-1.5 rounded-md bg-fill-1 px-2.5 py-2 text-xs text-muted-foreground"
        data-testid="intake-preview-loading"
      >
        <Loader2 className="size-3 animate-spin" />
        Loading the intake...
      </div>
    );
  }

  if (error || !data) {
    return (
      <div
        className="flex items-start gap-1.5 rounded-md bg-fill-1 px-2.5 py-2 text-xs text-muted-foreground"
        data-testid="intake-preview-error"
      >
        <AlertTriangle className="mt-0.5 size-3 shrink-0" />
        <span>
          Couldn&apos;t load the intake to preview it.
          {editHref ? (
            <>
              {" "}
              <Link
                to={editHref}
                target="_blank"
                rel="noreferrer"
                className="text-foreground underline underline-offset-2"
              >
                Open it in Intake Requests
              </Link>{" "}
              to check it before approving.
            </>
          ) : null}
        </span>
      </div>
    );
  }

  const { questions: total, minutes } = intakeEstimate(data.schema ?? []);
  const required = sections.reduce(
    (n, s) => n + s.questions.filter((q) => q.required).length,
    0
  );
  const titled = sections.filter((s) => s.title).length;
  const summary = [
    titled ? countLabel(titled, "section") : null,
    `${countLabel(total, "question")}${
      required ? ` (${required} required)` : ""
    }`,
    minutes ? `about ${countLabel(minutes, "minute")} for the client` : null
  ]
    .filter(Boolean)
    .join(", ");
  const uploads = sections
    .flatMap((s) => s.questions)
    .filter((q) => q.kind === TYPE_LABEL.file);

  return (
    <div
      className="flex flex-col gap-2 rounded-md bg-fill-1 px-2.5 py-2"
      data-testid="intake-approval-preview"
    >
      <div className="flex items-start gap-2">
        <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-muted">
          <ClipboardList className="size-3.5 text-muted-foreground" />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span
            className="text-xs font-medium text-foreground"
            data-testid="intake-preview-title"
          >
            {data.title || "Intake"}
          </span>
          <span
            className="text-xs text-muted-foreground"
            data-testid="intake-preview-summary"
          >
            {summary}.
            {expires ? ` Link expires in ${countLabel(expires, "day")}.` : null}
          </span>
        </div>
      </div>

      {uploads.length ? (
        <p
          className="text-xs text-foreground"
          data-testid="intake-preview-uploads"
        >
          <span className="text-muted-foreground">Asks for: </span>
          {uploads
            .slice(0, UPLOADS_SHOWN)
            .map((q) => q.prompt)
            .join("; ")}
          {uploads.length > UPLOADS_SHOWN
            ? `; and ${uploads.length - UPLOADS_SHOWN} more`
            : null}
        </p>
      ) : null}

      {total > LONG_INTAKE ? (
        <p
          className="flex items-start gap-1.5 rounded-md bg-warn-soft px-2 py-1.5 text-xs text-warn"
          data-testid="intake-preview-long"
        >
          <AlertTriangle className="mt-0.5 size-3 shrink-0" />
          <span>
            This is a long intake. If you only need the missing items, ask the
            assistant for a shorter request before you approve.
          </span>
        </p>
      ) : null}

      {note ? (
        <div className="flex flex-col gap-0.5">
          <span className="text-xs text-muted-foreground">
            Note shown to the client
          </span>
          <div
            className={cn(
              "border-l pl-2 text-xs whitespace-pre-wrap text-foreground",
              !noteOpen && "line-clamp-3"
            )}
            data-testid="intake-preview-note"
          >
            {note}
          </div>
          {note.length > 180 ? (
            <button
              type="button"
              className="self-start pl-2.5 text-xs font-medium text-muted-foreground hover:text-foreground"
              onClick={() => setNoteOpen((v) => !v)}
            >
              {noteOpen ? "Show less" : "Show all"}
            </button>
          ) : null}
        </div>
      ) : null}

      {total ? (
        <>
          <button
            type="button"
            className="inline-flex items-center gap-1 self-start text-xs font-medium text-muted-foreground hover:text-foreground"
            onClick={() => setShowOutline((v) => !v)}
            data-testid="intake-preview-toggle-outline"
          >
            {showOutline ? (
              <ChevronDown className="size-3" />
            ) : (
              <ChevronRight className="size-3" />
            )}
            {showOutline
              ? "Hide the questions"
              : `Show the ${countLabel(total, "question")}`}
          </button>
          {showOutline ? <Outline sections={sections} /> : null}
        </>
      ) : null}

      <div className="-mx-0.5 flex items-center gap-1">
        <Button
          variant="outline"
          size="xs"
          className="h-6 gap-1 px-2 text-xs"
          onClick={() => setDialogOpen(true)}
          data-testid="intake-preview-open-client"
        >
          <Eye className="size-3" /> Preview as client
        </Button>
        {editHref ? (
          <Button
            asChild
            variant="ghost"
            size="xs"
            className="h-6 gap-1 px-1.5 text-xs text-muted-foreground hover:text-foreground"
          >
            <Link to={editHref} target="_blank" rel="noreferrer">
              Open in Intake Requests <ExternalLink className="size-3" />
            </Link>
          </Button>
        ) : null}
      </div>

      {dialogOpen ? (
        <ClientPreviewDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          intake={data}
          note={note}
          clientName={clientName}
          editHref={editHref}
        />
      ) : null}
    </div>
  );
};
